import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from "react-redux";
import {getRecordingsThunk, updateRecordingThunk, bulkUpdateRecordingsThunk} from "../Feature/RecordingsSlice";

import {getLabelsThunk} from "../Feature/LabelsSlice";
import {getSongsThunk} from "../Feature/SongsSlice";
import {Card, message, Button, Space, Tag} from "antd";
import {PlusOutlined} from "@ant-design/icons";
import {useAdminMode} from "../hooks/useAdminMode";
import {createLabel} from "../Feature/API";
import moment from "moment";
import "./ViewRecordings.css"
import "../Styles/ActionButtons.css"
import {EditableLabelGroup} from "../Components/EditableLabelGroup";
import {SongSelector} from "../Components/SongSelector";
import {useParams} from "react-router-dom";
import {RecordingUploadModal} from "../Components/RecordingUploadModal";
import {BulkAssignModal} from "../Components/BulkAssignModal";
import {RecordingTable} from "../Components/RecordingTable";

export const ViewRecordings = () => {
    const dispatch = useDispatch();
    const { spaceId } = useParams();
    const { adminMode, toggleAdminMode } = useAdminMode();
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [bulkAssignModalVisible, setBulkAssignModalVisible] = useState(false);
    const [selectedRecordingIds, setSelectedRecordingIds] = useState([]);
    const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);

    const currentSpace = useSelector((state) => state.spaces.current);
    const currentUser = useSelector((state) => state.firebase.auth?.currentUser);
    const isOwner = currentSpace?.data?.spaceOwner?.uid === currentUser?.uid;

    const recordings = useSelector((state) => {
        return state.recordings.recordings
    })

    const labels = useSelector((state) => {
        return state.labels.labels
    })

    const songs = useSelector((state) => {
        return state.songs.songs
    })

    const user = useSelector((state) => {
        return state.firebase.auth
    });

    useEffect(() => {
        dispatch(getLabelsThunk(spaceId))
        dispatch(getSongsThunk(spaceId))
        dispatch(getRecordingsThunk(spaceId))

    }, [dispatch, spaceId, user]);

    const createLabels = (labels) => {
        return Promise.all(labels.map(label => createLabel(spaceId, {"label_name": label.label})))
            .then(async labels => {
                await dispatch(getLabelsThunk(spaceId));
                return labels.map(label => {
                    return {label: label.label_name, value: label.id}
                })
            })
    }

    const updateRecording = (record, labels) => {
        message.loading({content: `Updating record ${record.original_name}`, key: record.id});
        const existingLabels = labels.filter(label => label.value)
        createLabels(labels.filter(label => !label.value)).then(
            newLabels => {
                console.log("updating");
                return dispatch(updateRecordingThunk({
                    spaceId: spaceId, data: {
                        recording_id: record.id,
                        label_ids: [...newLabels, ...existingLabels].map(label => label.value)
                    }
                }))
            }).then(() => {
            console.log("reloading");
            return dispatch(getRecordingsThunk(spaceId))
        })
            .then(() => message.success({
                content: `Record ${record.original_name} was updated`,
                key: record.id
            }))


    }

    const updateRecordingSong = (record, songId) => {
        message.loading({content: `Updating record ${record.original_name}`, key: record.id});
        return dispatch(updateRecordingThunk({
            spaceId: spaceId, data: {
                recording_id: record.id,
                song_id: songId
            }
        })).then(() => {
            return dispatch(getRecordingsThunk(spaceId))
        }).then(() => message.success({
            content: `Record ${record.original_name} was updated`,
            key: record.id
        }))
    }

    const handleBulkAssignSong = async (songId) => {
        if (selectedRecordingIds.length === 0) {
            message.error('Please select at least one recording');
            return;
        }

        setBulkUpdateLoading(true);
        try {
            await dispatch(bulkUpdateRecordingsThunk({
                spaceId,
                recordingIds: selectedRecordingIds,
                songId
            })).unwrap();

            message.success(`Successfully assigned song to ${selectedRecordingIds.length} recording(s)`);
            setBulkAssignModalVisible(false);
            setSelectedRecordingIds([]);
            await dispatch(getRecordingsThunk(spaceId));
        } catch (error) {
            message.error('Failed to bulk update recordings: ' + (error.message || 'Unknown error'));
        } finally {
            setBulkUpdateLoading(false);
        }
    }

    const labelFilters = labels.map(label => {
        return {text: label.label_name, value: label.id}
    })

    const customColumns = [
        {
            title: 'Recording Date',
            dataIndex: 'creation_date_time',
            sorter: (a, b) => moment(a.creation_date_time).isBefore(moment(b.creation_date_time)),
            key: 'creation_date_time',
            render: datetime => moment(datetime).utc().format('YYYY-MM-DD'),
            responsive: ["sm"]
        },
        {
            title: 'Song',
            key: 'song',
            render: (text, record) => {
                return (<SongSelector songs={songs}
                                      selectedSong={record.song}
                                      onChange={(songId) => updateRecordingSong(record, songId)}/>)
            },
            responsive: ["sm"]
        },
        {
            title: 'Labels',
            key: 'labels',
            filters: [
                ...labelFilters
            ],
            onFilter: (value, record) => {
                return record.labels.some(label =>label.id.indexOf(value) === 0)
            },
            render: (text, record) => {
                return (<EditableLabelGroup key={record.labels.length} labels={labels}
                                            selectedLabels={record.labels}
                                            change={(labels) => updateRecording(record, labels)}/>)
            },
            responsive: ["sm"]
        }
    ]

    return (
        <>
            <div className="page-header-with-actions">
                <h1>Recordings</h1>
                <Button
                    className="action-button-primary action-button-large"
                    icon={<PlusOutlined />}
                    onClick={() => setUploadModalVisible(true)}
                >
                    Upload Recordings
                </Button>
            </div>

            {adminMode && selectedRecordingIds.length > 0 && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    backgroundColor: '#e6f7ff',
                    border: '1px solid #91d5ff',
                    borderRadius: '4px',
                    marginBottom: '16px'
                }}>
                    <span>
                        <Tag color="blue">{selectedRecordingIds.length}</Tag>
                        {selectedRecordingIds.length === 1 ? 'recording selected' : 'recordings selected'}
                    </span>
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => setBulkAssignModalVisible(true)}
                        >
                            Bulk Assign Song
                        </Button>
                        <Button
                            onClick={() => setSelectedRecordingIds([])}
                        >
                            Clear Selection
                        </Button>
                    </Space>
                </div>
            )}

            <RecordingTable
                key={labels.length}
                recordings={recordings}
                columns={customColumns}
                rowSelection={adminMode ? {
                    type: 'checkbox',
                    selectedRowKeys: selectedRecordingIds,
                    onChange: (selectedKeys) => setSelectedRecordingIds(selectedKeys)
                } : undefined}
            />

            <BulkAssignModal
                visible={bulkAssignModalVisible}
                onClose={() => setBulkAssignModalVisible(false)}
                selectedCount={selectedRecordingIds.length}
                songs={songs}
                onConfirm={handleBulkAssignSong}
                loading={bulkUpdateLoading}
            />

            <RecordingUploadModal
                visible={uploadModalVisible}
                onClose={() => setUploadModalVisible(false)}
                spaceId={spaceId}
                dispatch={dispatch}
            />
        </>
    );
}
